import React, { useState } from 'react';
import ColorPicker from './ColorPicker';

/**
 * BrandingSidebar - Collapsible sidebar for branding controls
 *
 * Controls:
 * - Primary & secondary colors
 * - Logo upload
 * - Font selection
 * - Social proof stats
 */
export default function BrandingSidebar({
  branding = {},
  onChange,
  isOpen = true,
  onToggle
}) {
  const [activeTab, setActiveTab] = useState('colors');

  const handleChange = (key, value) => {
    onChange({ ...branding, [key]: value });
  };

  const handleNestedChange = (parentKey, key, value) => {
    onChange({
      ...branding,
      [parentKey]: {
        ...(branding[parentKey] || {}),
        [key]: value
      }
    });
  };

  const fontOptions = [
    { value: 'Inter', label: 'Inter (Modern)' },
    { value: 'Roboto', label: 'Roboto (Clean)' },
    { value: 'Playfair Display', label: 'Playfair (Elegant)' },
    { value: 'Montserrat', label: 'Montserrat (Bold)' },
    { value: 'Open Sans', label: 'Open Sans (Friendly)' },
    { value: 'Lato', label: 'Lato (Professional)' },
    { value: 'Poppins', label: 'Poppins (Geometric)' },
  ];

  return (
    <div
      className={`bg-white border-r border-gray-200 h-full transition-all duration-300 flex flex-col ${
        isOpen ? 'w-80' : 'w-12'
      }`}
    >
      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="flex items-center justify-center h-12 border-b border-gray-200 hover:bg-gray-50 transition-colors"
      >
        {isOpen ? (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        )}
      </button>

      {isOpen && (
        <>
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Branding</h3>
            <p className="text-xs text-gray-500 mt-0.5">Customize your funnel's look</p>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200">
            {[
              { id: 'colors', label: 'Colors', icon: '🎨' },
              { id: 'fonts', label: 'Fonts', icon: '𝐀' },
              { id: 'logo', label: 'Logo', icon: '🖼️' },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-3 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50/50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className="mr-1">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Colors Tab */}
            {activeTab === 'colors' && (
              <>
                <ColorPicker
                  label="Primary Color"
                  value={branding.primary_color || '#1e40af'}
                  onChange={(color) => handleChange('primary_color', color)}
                />

                <ColorPicker
                  label="Accent Color"
                  value={branding.secondary_color || '#AEFFE3'}
                  onChange={(color) => handleChange('secondary_color', color)}
                />

                <ColorPicker
                  label="Text Color"
                  value={branding.text_color || '#1f2937'}
                  onChange={(color) => handleChange('text_color', color)}
                />

                <ColorPicker
                  label="Background Color"
                  value={branding.background_color || '#ffffff'}
                  onChange={(color) => handleChange('background_color', color)}
                />

                {/* Quick presets */}
                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quick Presets
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      {
                        name: 'Professional Blue',
                        colors: { primary_color: '#1e40af', secondary_color: '#AEFFE3' }
                      },
                      {
                        name: 'Modern Purple',
                        colors: { primary_color: '#7c3aed', secondary_color: '#fbbf24' }
                      },
                      {
                        name: 'Trust Green',
                        colors: { primary_color: '#047857', secondary_color: '#fcd34d' }
                      },
                      {
                        name: 'Bold Red',
                        colors: { primary_color: '#dc2626', secondary_color: '#fef08a' }
                      },
                    ].map((preset) => (
                      <button
                        key={preset.name}
                        onClick={() => onChange({ ...branding, ...preset.colors })}
                        className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                      >
                        <div className="flex -space-x-1">
                          <div
                            className="w-4 h-4 rounded-full border border-white"
                            style={{ backgroundColor: preset.colors.primary_color }}
                          />
                          <div
                            className="w-4 h-4 rounded-full border border-white"
                            style={{ backgroundColor: preset.colors.secondary_color }}
                          />
                        </div>
                        <span className="truncate">{preset.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Fonts Tab */}
            {activeTab === 'fonts' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Heading Font
                  </label>
                  <select
                    value={branding.fonts?.heading || 'Inter'}
                    onChange={(e) => handleNestedChange('fonts', 'heading', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {fontOptions.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                  <p
                    className="mt-2 text-lg text-gray-600"
                    style={{ fontFamily: branding.fonts?.heading || 'Inter' }}
                  >
                    Preview Heading Text
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Body Font
                  </label>
                  <select
                    value={branding.fonts?.body || 'Inter'}
                    onChange={(e) => handleNestedChange('fonts', 'body', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {fontOptions.map((font) => (
                      <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                        {font.label}
                      </option>
                    ))}
                  </select>
                  <p
                    className="mt-2 text-sm text-gray-600"
                    style={{ fontFamily: branding.fonts?.body || 'Inter' }}
                  >
                    Preview body text in your selected font.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Button Style
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'rounded', label: 'Rounded', className: 'rounded-xl' },
                      { id: 'square', label: 'Square', className: 'rounded-md' },
                      { id: 'pill', label: 'Pill', className: 'rounded-full' },
                      { id: 'sharp', label: 'Sharp', className: 'rounded-none' },
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => handleChange('button_style', style.id)}
                        className={`px-4 py-2 text-sm font-medium text-white ${style.className} transition-all ${
                          branding.button_style === style.id
                            ? 'ring-2 ring-offset-2 ring-blue-500'
                            : ''
                        }`}
                        style={{ backgroundColor: branding.primary_color || '#1e40af' }}
                      >
                        {style.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Logo Tab */}
            {activeTab === 'logo' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Logo URL
                  </label>
                  <input
                    type="url"
                    value={branding.logo_url || ''}
                    onChange={(e) => handleChange('logo_url', e.target.value)}
                    placeholder="https://example.com/logo.svg"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                {branding.logo_url && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preview
                    </label>
                    <div className="p-4 bg-gray-100 rounded-lg flex items-center justify-center">
                      <img
                        src={branding.logo_url}
                        alt="Logo preview"
                        className="max-h-16 max-w-full object-contain"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="40"><text x="10" y="28" fill="%23666">Logo Error</text></svg>';
                        }}
                      />
                    </div>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Favicon URL
                  </label>
                  <input
                    type="url"
                    value={branding.favicon_url || ''}
                    onChange={(e) => handleChange('favicon_url', e.target.value)}
                    placeholder="https://example.com/favicon.ico"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Company Name
                  </label>
                  <input
                    type="text"
                    value={branding.company_name || ''}
                    onChange={(e) => handleChange('company_name', e.target.value)}
                    placeholder="Your Company Name"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}
          </div>

          {/* Footer with reset button */}
          <div className="p-4 border-t border-gray-200">
            <button
              onClick={() => {
                if (confirm('Reset all branding to defaults?')) {
                  onChange({
                    primary_color: '#1e40af',
                    secondary_color: '#AEFFE3',
                    text_color: '#1f2937',
                    background_color: '#ffffff',
                    fonts: { heading: 'Inter', body: 'Inter' },
                    button_style: 'rounded'
                  });
                }
              }}
              className="w-full px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Reset to Defaults
            </button>
          </div>
        </>
      )}
    </div>
  );
}
